<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="sub-section">
	<div class="sub-section-header">
		<span class="sub-section-title">2. Configuration</span>

		<span class="sub-section-description">
			Complete all the elements of the first column and at least the details of one hardware
			component in the other columns to be able to proceed with the calculation.
		</span>
	</div>

	<form
		class="configuration-form"
		[formGroup]="configurationForm"
	>
		<div class="form-grid">
			<div class="form-cards">
				<div class="form-card no-bg">
					<div class="form-card-header">
						<span class="form-card-title">Running interval</span>
					</div>

					<globalictcc-datepicker
						label="Period"
						formControlName="timePeriod"
						class="fill"
					/>
				</div>

				<div class="form-card">
					<div class="form-card-header">
						<span class="form-card-title">Computing platform data</span>
					</div>

					<div class="adjacent-inputs">
						<globalictcc-dropdown
							label="Type"
							formControlName="computingPlatformType"
							class="fill"
							[options]="platformTypes"
						/>

						@if (isOnPremise) {
							<globalictcc-input
								label="PUE"
								formControlName="pue"
								class="fill"
								inputType="number"
								[min]="1"
								[max]="3"
								[step]="0.01"
							/>
						} @else {
							<globalictcc-dropdown
								label="Cloud provider"
								formControlName="cloudProvider"
								class="fill"
								[options]="cloudProviders"
							/>
						}
					</div>

					<div class="adjacent-inputs">
						<globalictcc-dropdown
							label="Areas"
							formControlName="computingPlatformAreas"
							class="fill"
							[options]="areas"
						/>

						<globalictcc-dropdown
							label="Regions"
							formControlName="computingPlatformRegions"
							class="fill"
							[options]="regions"
						/>
					</div>
				</div>
			</div>

			<div class="form-cards">
				<div class="form-card">
					<div class="form-card-header">
						<span class="form-card-title">CPU data</span>
					</div>

					<globalictcc-dropdown
						label="CPU model"
						formControlName="cpuModel"
						class="fill"
						[options]="availableCpuModels"
					/>

					<div class="adjacent-inputs">
						<globalictcc-dropdown
							label="Number of cores"
							formControlName="cpuCores"
							class="fill"
							[options]="numberOfCores"
						/>

						<globalictcc-input
							label="Load"
							formControlName="cpuLoad"
							class="fill"
							inputType="number"
							[min]="0.01"
							[max]="1"
							[step]="0.01"
						/>
					</div>
				</div>

				<div class="form-card">
					<div class="form-card-header">
						<span class="form-card-title">Storage data</span>
					</div>

					<globalictcc-dropdown
						label="Storage type"
						formControlName="storageType"
						class="fill"
						[options]="storageTypes"
					/>

					<globalictcc-input
						label="Size"
						formControlName="storageSize"
						class="fill"
						inputType="number"
						unit="GB"
						[min]="1"
					/>
				</div>

				@if (isOnPremise) {
					<div class="form-card">
						<div class="form-card-header">
							<span class="form-card-title">GPU data</span>
						</div>

						<globalictcc-dropdown
							label="GPU model"
							formControlName="gpuModel"
							class="fill"
							[options]="availableGpuModels"
						/>

						<div class="adjacent-inputs">
							<globalictcc-input
								label="Number of cores"
								formControlName="gpuCores"
								class="fill"
								inputType="number"
								[min]="1"
								[max]="100"
							/>

							<globalictcc-input
								label="Load"
								formControlName="gpuLoad"
								class="fill"
								inputType="number"
								[min]="0"
								[max]="1"
								[step]="0.01"
							/>
						</div>
					</div>
				}

				<div class="form-card">
					<div class="form-card-header">
						<span class="form-card-title">Memory data</span>
					</div>

					<globalictcc-input
						label="Size"
						formControlName="memorySize"
						class="fill"
						inputType="number"
						unit="GB"
						[min]="1"
					/>
				</div>
			</div>
		</div>

		<div class="form-actions">
			<globalictcc-button
				label="Calculate"
				[disabled]="!configurationForm.valid"
				(handleClick)="onCalculate()"
			/>

			<globalictcc-button
				label="Reset all changes"
				color="secondary"
				(handleClick)="onResetForm()"
			/>
		</div>
	</form>
</section>

@if (hasCalculated) {
	<section class="sub-section">
		<div class="sub-section-header">
			<span class="sub-section-title">3. Results</span>
		</div>

		<div class="charts">
			<globalictcc-chart
				chartType="barX"
				titleSize="small"
				[data]="emissionChart?.chart"
				[isLoading]="isEmissionChartLoading"
				[hasError]="emissionChart?.hasError"
				[configOverrides]="emissionOverrides"
				(handleChartRefresh)="onCalculate()"
			/>

			<globalictcc-chart
				chartType="barY"
				titleSize="small"
				[data]="simulationChart?.chart"
				[isLoading]="isSimulationChartLoading"
				[hasError]="simulationChart?.hasError"
				[configOverrides]="simulationOverrides"
				(handleChartRefresh)="onCalculate()"
			/>
		</div>
	</section>
}
